import React from 'react';
import { ArrowDownTrayIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Invoice } from '../../services/invoice.service';
import { useInvoice } from '../../hooks/useInvoices';
import { Modal } from '../ui/Modal';
import {
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  formatMoney,
  formatNumber,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { GST_TYPE_LABELS, INVOICE_TYPE_LABELS } from './InvoiceForm';
import { INVOICE_STATUS_META, formatInvoiceDate, getDaysOverdue, getEffectiveStatus } from './invoiceDisplay';

interface ViewInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The list row; the lines are loaded when the dialog opens
  invoice: Invoice | null;
  onDownloadPDF: (invoice: Invoice) => void;
  onEdit: (invoice: Invoice) => void;
  isDownloading?: boolean;
}

const thClass = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const ViewInvoiceModal: React.FC<ViewInvoiceModalProps> = ({
  isOpen,
  onClose,
  invoice: row,
  onDownloadPDF,
  onEdit,
  isDownloading = false,
}) => {
  const { data: detail, isLoading } = useInvoice(isOpen && row ? row.id : null);
  // The full invoice once loaded; until then the row fills the header
  const invoice = detail && row && detail.id === row.id ? detail : row;

  if (!invoice) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Invoice Details" size="xl">
        <div />
      </Modal>
    );
  }

  const status = getEffectiveStatus(invoice);
  const items = detail && detail.id === invoice.id ? detail.items ?? [] : [];
  const isSplit = invoice.gstType === 'cgst_sgst';
  const isTaxed = isSplit || invoice.gstType === 'igst';
  const hasCess = invoice.totalCess > 0;
  const columnCount = 7 + (isSplit ? 2 : isTaxed ? 1 : 0) + (hasCess ? 1 : 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invoice Details" size="2xl">
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p>
              <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                {invoice.invoiceNumber}
              </span>
            </p>
            <h4 className="mt-2 text-lg font-semibold text-gray-900">{invoice.customerName}</h4>
            <p className="mt-1 text-sm text-gray-600">
              {INVOICE_TYPE_LABELS[invoice.invoiceType]} dated {formatInvoiceDate(invoice.invoiceDate)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {invoice.eInvoiceIRN && (
              <span className={`${badgeClass} text-purple-700 bg-purple-50 ring-purple-600/20`}>E-invoice</span>
            )}
            <span className={`${badgeClass} ${INVOICE_STATUS_META[status].color}`}>
              {INVOICE_STATUS_META[status].label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: 'Grand total', value: formatMoney(invoice.grandTotal), tone: 'bg-indigo-50 text-indigo-900' },
            { label: 'Taxable amount', value: formatMoney(invoice.totalTaxableAmount), tone: 'bg-gray-50 text-gray-900' },
            {
              label: 'GST',
              value: formatMoney(invoice.totalCGST + invoice.totalSGST + invoice.totalIGST + invoice.totalCess),
              tone: 'bg-gray-50 text-gray-900',
            },
            {
              label: 'Due date',
              value: formatInvoiceDate(invoice.dueDate),
              hint: status === 'overdue' ? `${getDaysOverdue(invoice)} days overdue` : undefined,
              tone: status === 'overdue' ? 'bg-red-50 text-red-900' : 'bg-gray-50 text-gray-900',
            },
          ].map((item) => (
            <div key={item.label} className={`rounded-lg px-4 py-3 ${item.tone}`}>
              <p className="text-xs font-medium uppercase tracking-wide opacity-70">{item.label}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">{item.value}</p>
              {item.hint && <p className="text-xs font-medium">{item.hint}</p>}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <DetailCard title="Seller">
            <DetailItem label="Name" className="col-span-2">
              {invoice.supplierName}
            </DetailItem>
            <DetailItem label="GST number">
              <span className="font-mono">{invoice.supplierGSTIN}</span>
            </DetailItem>
            <DetailItem label="State">
              {invoice.supplierState} ({invoice.supplierStateCode})
            </DetailItem>
            <DetailItem label="Address" className="col-span-2">
              <span className="font-normal text-gray-700">{invoice.supplierAddress}</span>
            </DetailItem>
          </DetailCard>

          <DetailCard title="Bill to">
            <DetailItem label="Name" className="col-span-2">
              {invoice.customerName}
            </DetailItem>
            <DetailItem label="GST number">
              {invoice.customerGSTIN ? <span className="font-mono">{invoice.customerGSTIN}</span> : 'Unregistered'}
            </DetailItem>
            <DetailItem label="State">
              {invoice.customerState} ({invoice.customerStateCode})
            </DetailItem>
            <DetailItem label="Address" className="col-span-2">
              <span className="font-normal text-gray-700">{invoice.customerAddress}</span>
            </DetailItem>
          </DetailCard>
        </div>

        <DetailCard title="Supply" columns={4}>
          <DetailItem label="Place of supply">
            {invoice.placeOfSupply} ({invoice.placeOfSupplyCode})
          </DetailItem>
          <DetailItem label="Tax type">{GST_TYPE_LABELS[invoice.gstType]}</DetailItem>
          <DetailItem label="Reverse charge">{invoice.reverseCharge ? 'Yes' : 'No'}</DetailItem>
          <DetailItem label="Last updated">{formatInvoiceDate(invoice.updatedAt)}</DetailItem>
        </DetailCard>

        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>#</th>
                <th className={thClass}>Item</th>
                <th className={thClass}>HSN</th>
                <th className={`${thClass} text-right`}>Qty</th>
                <th className={`${thClass} text-right`}>Rate</th>
                <th className={`${thClass} text-right`}>Disc.</th>
                <th className={`${thClass} text-right`}>Taxable</th>
                {isSplit && <th className={`${thClass} text-right`}>CGST</th>}
                {isSplit && <th className={`${thClass} text-right`}>SGST</th>}
                {!isSplit && isTaxed && <th className={`${thClass} text-right`}>IGST</th>}
                {hasCess && <th className={`${thClass} text-right`}>Cess</th>}
                <th className={`${thClass} text-right`}>Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading || items.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-center text-sm text-gray-500" colSpan={columnCount + 1}>
                    {isLoading ? 'Loading items...' : 'This invoice has no items'}
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={item.id}>
                    <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{item.productName}</td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-700">{item.hsnCode}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                      {formatNumber(item.quantity)} {item.unit}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                      {formatMoney(item.rate)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                      {item.discount ? `${formatNumber(item.discount, 2)}%` : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                      {formatMoney(item.taxableAmount)}
                    </td>
                    {isSplit && (
                      <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                        {formatMoney(item.cgstAmount)}
                        <span className="block text-xs text-gray-500">{formatNumber(item.cgstRate, 2)}%</span>
                      </td>
                    )}
                    {isSplit && (
                      <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                        {formatMoney(item.sgstAmount)}
                        <span className="block text-xs text-gray-500">{formatNumber(item.sgstRate, 2)}%</span>
                      </td>
                    )}
                    {!isSplit && isTaxed && (
                      <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                        {formatMoney(item.igstAmount)}
                        <span className="block text-xs text-gray-500">{formatNumber(item.igstRate, 2)}%</span>
                      </td>
                    )}
                    {hasCess && (
                      <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-700 tabular-nums">
                        {formatMoney(item.cessAmount)}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm font-medium text-gray-900 tabular-nums">
                      {formatMoney(item.totalAmount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="space-y-5">
            {invoice.amountInWords && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Amount in words</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{invoice.amountInWords}</p>
              </div>
            )}
            {invoice.bankName && (
              <DetailCard title="Bank details">
                <DetailItem label="Bank">{invoice.bankName}</DetailItem>
                <DetailItem label="Branch">{invoice.bankBranch || '—'}</DetailItem>
                <DetailItem label="Account number">
                  <span className="font-mono">{invoice.bankAccountNumber || '—'}</span>
                </DetailItem>
                <DetailItem label="IFSC">
                  <span className="font-mono">{invoice.bankIFSC || '—'}</span>
                </DetailItem>
              </DetailCard>
            )}
            {(invoice.notes || invoice.termsAndConditions) && (
              <DetailCard title="Notes and terms">
                {invoice.notes && (
                  <DetailItem label="Notes" className="col-span-2">
                    <span className="whitespace-pre-line font-normal text-gray-700">{invoice.notes}</span>
                  </DetailItem>
                )}
                {invoice.termsAndConditions && (
                  <DetailItem label="Terms and conditions" className="col-span-2">
                    <span className="whitespace-pre-line font-normal text-gray-700">{invoice.termsAndConditions}</span>
                  </DetailItem>
                )}
              </DetailCard>
            )}
            {invoice.eInvoiceIRN && (
              <DetailCard title="E-invoice">
                <DetailItem label="IRN" className="col-span-2 break-all">
                  <span className="font-mono text-xs">{invoice.eInvoiceIRN}</span>
                </DetailItem>
                <DetailItem label="Acknowledgement">{invoice.eInvoiceAckNumber || '—'}</DetailItem>
                <DetailItem label="Acknowledged on">{formatInvoiceDate(invoice.eInvoiceAckDate)}</DetailItem>
              </DetailCard>
            )}
          </div>

          <section className="self-start rounded-lg border border-gray-200 bg-white">
            <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Totals
            </h4>
            <dl className="space-y-2 p-4 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-600">Taxable amount</dt>
                <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(invoice.totalTaxableAmount)}</dd>
              </div>
              {invoice.totalDiscount > 0 && (
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">after a discount of</dt>
                  <dd className="text-gray-500 tabular-nums">{formatMoney(invoice.totalDiscount)}</dd>
                </div>
              )}
              {isSplit && (
                <>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">CGST</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(invoice.totalCGST)}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">SGST</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(invoice.totalSGST)}</dd>
                  </div>
                </>
              )}
              {!isSplit && isTaxed && (
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">IGST</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(invoice.totalIGST)}</dd>
                </div>
              )}
              {hasCess && (
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Cess</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(invoice.totalCess)}</dd>
                </div>
              )}
              {invoice.roundOff !== 0 && (
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Round off</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(invoice.roundOff)}</dd>
                </div>
              )}
              <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-3">
                <dt className="font-semibold text-gray-900">Grand total</dt>
                <dd className="text-xl font-semibold text-gray-900 tabular-nums">{formatMoney(invoice.grandTotal)}</dd>
              </div>
            </dl>
          </section>
        </div>
      </div>

      <FormActions>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
        <button
          type="button"
          onClick={() => onDownloadPDF(invoice)}
          disabled={isDownloading}
          className={invoice.status === 'draft' ? secondaryButtonClass : primaryButtonClass}
        >
          <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
          {isDownloading ? 'Preparing PDF...' : 'Download PDF'}
        </button>
        {invoice.status === 'draft' && (
          <button type="button" onClick={() => onEdit(invoice)} className={primaryButtonClass}>
            <PencilIcon className="mr-2 h-4 w-4" />
            Edit Draft
          </button>
        )}
      </FormActions>
    </Modal>
  );
};

export default ViewInvoiceModal;
